import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { Button } from '@/kit/Button';
import { DataTable, type DataTableColumn } from '@/kit/DataTable';
import { PageHeader } from '@/kit/PageHeader';
import { Select } from '@/kit/Select';
import { SideMenuPage } from '@/features/taxreturn/menus/SideMenuPage';
import { NotificationTextMessage } from '@/core/enums/notification';
import { notifyError, notifySuccess } from '@/core/notify/notify';
import { selectedClient, type SelectedClient } from '@/core/selectedClient';
import { partnerAllocationService } from '@/core/services/partnerAllocation';
import { practiceService } from '@/core/services/practice';
import type { PeriodRow } from '@/core/models/practice.models';
import {
  formatPartnerMoney,
  toAmount,
  type PartnerMemberBalanceInput,
  type PartnerMemberBalanceRow,
  type PartnerMemberBalanceScreen,
} from '@/core/models/partner-allocation.models';
import { AmountInput } from './AmountInput';
import { periodLabel } from './format';
import { useMounted } from './useMounted';
import './LeafTables.scss';
import './TaxreturnPartnerAccounts.scss';

type BalanceField = 'openingBalance' | 'introduced' | 'drawings' | 'otherMovement';

/** A balance row plus a stable key (partner x account kind). */
type BalanceEditRow = PartnerMemberBalanceRow & { key: number };

const PERIODS_ROUTE = '/home/taxreturns/tasks-accountingperiods';

export function TaxreturnPartnerAccounts() {
  const navigate = useNavigate();
  const mounted = useMounted();
  const [client] = useState<SelectedClient | null>(() => selectedClient());
  const nextKey = useRef(1);

  const [screen, setScreen] = useState<PartnerMemberBalanceScreen | null>(null);
  const [rows, setRows] = useState<BalanceEditRow[]>([]);
  const [periods, setPeriods] = useState<PeriodRow[]>([]);
  const [selectedPeriodId, setSelectedPeriodId] = useState<string | null>(null);
  /** True while Save is running, so it cannot be sent twice. */
  const [saving, setSaving] = useState(false);

  const entityLabel = screen?.entityLabel ?? 'partner';
  const entityLabelTitle = entityLabel.charAt(0).toUpperCase() + entityLabel.slice(1);
  const isApplicable = screen?.isPartnershipOrLlp === true;
  const canEdit = isApplicable && screen?.isLocked !== true;

  const apply = useCallback((next: PartnerMemberBalanceScreen) => {
    setScreen(next);
    setSelectedPeriodId(next.accountingPeriodId);
    setRows(next.rows.map((r) => ({ ...r, key: nextKey.current++ })));
  }, []);

  const load = useCallback(
    (periodId: string | null) => {
      if (!client) return;
      partnerAllocationService
        .getBalances(client.id, periodId)
        .then((result) => {
          if (mounted.current) apply(result);
        })
        .catch((error) => {
          if (mounted.current) notifyError(error);
        });
    },
    [client, apply, mounted],
  );

  useEffect(() => {
    if (!client) {
      notifyError('Select a client from Accounts first.');
      navigate('/home/accountclient');
      return;
    }
    practiceService
      .getClientPeriods(client.id)
      .then((result) => {
        if (mounted.current) setPeriods(result.periods ?? []);
      })
      .catch(() => {
        if (mounted.current) setPeriods([]);
      });
    load(null);
    // Runs once, like ngOnInit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const money = (value: number) => formatPartnerMoney(value);

  /** Recomputes the closing balance as the user types, the same way the database computes it. */
  const onAmountChange = (key: number, field: BalanceField, value: number) =>
    setRows((current) =>
      current.map((row) => {
        if (row.key !== key) return row;
        const next = { ...row, [field]: value };
        next.closingBalance =
          next.openingBalance +
          next.introduced +
          next.allocationMovement +
          next.interestMovement +
          next.otherMovement -
          next.drawings;
        return next;
      }),
    );

  const save = () => {
    const periodId = screen?.accountingPeriodId;
    if (!client || !periodId) {
      notifyError('Open a client from Accounts first.');
      return;
    }
    if (!canEdit) return;

    const inputs: PartnerMemberBalanceInput[] = rows.map((r) => ({
      relationshipId: r.relationshipId,
      accountKind: r.accountKind,
      openingBalance: toAmount(r.openingBalance),
      introduced: toAmount(r.introduced),
      drawings: toAmount(r.drawings),
      otherMovement: toAmount(r.otherMovement),
    }));

    setSaving(true);
    partnerAllocationService
      .saveBalances({ clientId: client.id, accountingPeriodId: periodId, rows: inputs })
      .then((result) => {
        if (!mounted.current) return;
        apply(result);
        notifySuccess(NotificationTextMessage.successMessage);
      })
      .catch((error) => {
        if (mounted.current) notifyError(error);
      })
      .finally(() => {
        if (mounted.current) setSaving(false);
      });
  };

  const inputColumn = (id: string, header: string, field: BalanceField): DataTableColumn<BalanceEditRow> => ({
    id,
    header,
    headerClassName: 'w-120',
    cell: (row) => (
      <AmountInput
        fieldClassName="w-120"
        disabled={!canEdit}
        value={row[field]}
        onChange={(value) => onAmountChange(row.key, field, value)}
      />
    ),
  });

  const readOnlyColumn = (
    id: string,
    header: string,
    read: (row: BalanceEditRow) => number,
  ): DataTableColumn<BalanceEditRow> => ({
    id,
    header,
    headerClassName: 'w-120',
    cell: (row) => <div className="white-space-nowrap">{money(read(row))}</div>,
  });

  const columns: DataTableColumn<BalanceEditRow>[] = [
    {
      id: 'name',
      header: entityLabelTitle,
      headerClassName: 'w-200',
      cell: (row) => (
        <div className="d-flex">
          <div className="onelineclamp">{row.fullName}</div>
        </div>
      ),
    },
    {
      id: 'account',
      header: 'Account',
      headerClassName: 'w-150',
      cell: (row) => <div className="white-space-nowrap">{row.accountKindName}</div>,
    },
    inputColumn('opening', 'Opening', 'openingBalance'),
    inputColumn('introduced', 'Introduced', 'introduced'),
    readOnlyColumn('allocation', 'Profit allocated', (row) => row.allocationMovement),
    readOnlyColumn('interest', 'Interest', (row) => row.interestMovement),
    inputColumn('drawings', 'Drawings', 'drawings'),
    inputColumn('other', 'Other', 'otherMovement'),
    readOnlyColumn('closing', 'Closing', (row) => row.closingBalance),
  ];

  return (
    <SideMenuPage menu="task" contentClassName="taxreturn-partner-accounts">
      {/* Design review 2 Nov 2026: Save and Close are page actions, so they sit top-right in the sticky page header. */}
      <PageHeader title={`${entityLabelTitle} Accounts`}>
        <Button className="action-button primary-button border-radius-6" disabled={!canEdit} busy={saving} onClick={save}>
          <span>Save</span>
        </Button>
        <Button className="action-button secondary-button" onClick={() => navigate(PERIODS_ROUTE)}>
          <span>Close</span>
        </Button>
      </PageHeader>
      <div className="content-body">
        <div className="content-header mb-0">
          <div className="content-header-left mb-1">
            <Select<PeriodRow>
              className="w-250"
              openOnFocus
              panelClass="myPanelClass"
              items={periods}
              bindValue="id"
              clearable={false}
              searchable={false}
              placeholder="Accounting period"
              value={selectedPeriodId}
              onChange={(value) => {
                const periodId = value as string | null;
                setSelectedPeriodId(periodId);
                load(periodId);
              }}
              renderLabel={periodLabel}
              renderOption={periodLabel}
            />
          </div>
        </div>

        {screen && !isApplicable && (
          <div className="not-applicable">
            <p>
              Capital, current and loan accounts apply to partnerships and LLPs only.{' '}
              <strong>{screen.clientName}</strong> is not one of those. Change the client type in Manage Clients, or
              select a partnership or LLP client.
            </p>
          </div>
        )}

        {screen && isApplicable && (
          <>
            <div className="d-flex align-items-center reconciliation-bar mb-1">
              <div>
                <strong>Period:</strong> {screen.periodLabel || 'No accounting period'}
              </div>
              <div>
                <strong>Total closing:</strong> {money(screen.reconciliation.totalClosingBalance)}
              </div>
              <div>
                <strong>Trial balance capital and reserves:</strong> {money(screen.reconciliation.equityNet)}
              </div>
              <div>
                <strong>Difference:</strong> {money(screen.reconciliation.difference)}
              </div>
              <div className={`status-pill ${screen.reconciliation.isReconciled ? 'reconciled' : 'not-reconciled'}`}>
                {screen.reconciliation.status}
              </div>
            </div>

            {screen.rows.length === 0 && (
              <p>No {entityLabel}s have been recorded for this client yet. Add them on the Profit Allocation screen.</p>
            )}
            {screen.isLocked && (
              <p>The accounting period is locked. Unlock it from Accounting Periods before changing these balances.</p>
            )}
            <p>
              The profit allocated and the interest on capital columns come from the Profit Allocation screen and are
              updated when the allocation is calculated.
            </p>

            <div className="mat-table-wrapper">
              <DataTable
                stickyHeader
                className="tr-leaf-sticky-header"
                columns={columns}
                rows={rows}
                rowKey={(row) => row.key}
              />
            </div>
          </>
        )}
      </div>
    </SideMenuPage>
  );
}
